/**
 * Date/time utilities for date-selekt.
 *
 * WIRE FORMAT (everything from/to the server): Postgres
 *   date         'YYYY-MM-DD'               (type=date — a calendar date has
 *                                            no timezone; never an instant)
 *   timestamptz  'YYYY-MM-DD HH:mm:ss±off'  (also parses ISO 'T' and 'Z')
 *   timetz       'HH:mm:ss±off'
 * Timestamp/time output carries the browser's local offset; parsed offsets
 * are converted to local time. parsePgTimestamp accepts the bare date too.
 *
 * DISPLAY FORMAT comes from g.settings (separate from the language):
 *   g.settings.dateFormat (default 'DD.MM.YYYY'), g.settings.timeFormat
 *   (default 'HH:mm'). Tokens: YYYY MM DD HH (24h) hh (12h) mm A (AM/PM).
 * Month/weekday names come from Intl using g.lang.
 */
export const pad = (n) => String(n).padStart(2, '0');

export function localOffsetString(date) {
	const min = -date.getTimezoneOffset();
	const sign = min >= 0 ? '+' : '-';
	const abs = Math.abs(min);
	return `${sign}${pad(Math.floor(abs / 60))}${abs % 60 ? ':' + pad(abs % 60) : ''}`;
}

// Date -> 'YYYY-MM-DD' (the PG date wire format — no time, no offset)
export function toPgDate(date) {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Date -> 'YYYY-MM-DD HH:mm:ss+02' (local time + local offset)
export function toPgTimestamp(date) {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${localOffsetString(date)}`;
}

// {h,m} -> '14:30:00+02' (current local offset)
export function toPgTime(time) {
	return `${pad(time.h)}:${pad(time.m)}:00${localOffsetString(new Date())}`;
}

const normalizeOffset = (off) => {
	const m = off.match(/^([+-])(\d{2}):?(\d{2})?$/);
	return m ? `${m[1]}${m[2]}:${m[3] || '00'}` : off;
};

// 'YYYY-MM-DD[ HH:mm[:ss]][±off]' -> local Date | null
export function parsePgTimestamp(str) {
	if (!str) return null;
	const m = String(str).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?)?$/);
	if (!m) return null;
	const [, y, mo, d, h = '00', mi = '00', s = '00', off] = m;
	if (!off) return new Date(+y, +mo - 1, +d, +h, +mi, +s);
	const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}${off === 'Z' ? 'Z' : normalizeOffset(off)}`);
	return isNaN(date) ? null : date;
}

// 'HH:mm[:ss][±off]' -> { h, m } in local time (offset resolved on today's date)
export function parsePgTime(str) {
	if (!str) return null;
	const m = String(str).trim().match(/^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/);
	if (!m) return null;
	const [, h, mi, , off] = m;
	if (!off) return { h: +h, m: +mi };
	const now = new Date();
	const date = new Date(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${h}:${mi}:00${off === 'Z' ? 'Z' : normalizeOffset(off)}`);
	return isNaN(date) ? { h: +h, m: +mi } : { h: date.getHours(), m: date.getMinutes() };
}

export function formatDisplay(fmt, date) {
	const h24 = date.getHours();
	return fmt
		.replace(/YYYY/g, String(date.getFullYear()))
		.replace(/MM/g, pad(date.getMonth() + 1))
		.replace(/DD/g, pad(date.getDate()))
		.replace(/HH/g, pad(h24))
		.replace(/hh/g, pad(h24 % 12 || 12))
		.replace(/mm/g, pad(date.getMinutes()))
		.replace(/A/g, h24 < 12 ? 'AM' : 'PM');
}

export function formatTimeDisplay(fmt, time) {
	return formatDisplay(fmt, new Date(2000, 0, 1, time.h, time.m));
}

export const localeOf = (lang) => String(lang || 'en').replace('_', '-');

export function monthLabel(date, lang) {
	return new Intl.DateTimeFormat(localeOf(lang), { month: 'long', year: 'numeric' }).format(date);
}

export function monthNamesShort(lang) {
	const fmt = new Intl.DateTimeFormat(localeOf(lang), { month: 'short' });
	return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2024, i, 1)));
}

// weekStart: 0 = Sunday, 1 = Monday (2024-01-07 was a Sunday)
export function weekdayNames(lang, weekStart) {
	const fmt = new Intl.DateTimeFormat(localeOf(lang), { weekday: 'short' });
	return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 7 + ((weekStart + i) % 7))));
}

export const sameDay = (a, b) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const dayOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// min/max option: Postgres string, 'today' or 'today±N' (days) -> Date | null
export function parseBound(value) {
	if (!value) return null;
	const rel = String(value).match(/^today([+-]\d+)?$/);
	if (rel) {
		const d = dayOnly(new Date());
		if (rel[1]) d.setDate(d.getDate() + parseInt(rel[1], 10));
		return d;
	}
	const parsed = parsePgTimestamp(value);
	return parsed ? dayOnly(parsed) : null;
}
